import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  Form,
  Row,
  Col,
  Button,
  Input,
  Spin,
  Table,
  Switch,
  Popconfirm,
  Modal,
  message,
} from "antd";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import JSZip from "jszip";
import ReactDOM from "react-dom/client";

import { BASE_URL } from "../../utils/constants";
import Layout from "../../components/Layout";
import { showErrorModal } from "../../components/Modal";
import api from "../../api/api";
import { LinkProfile, LinkProfileCreate } from "../../models";
import { useLinkProfile } from "../../api/useLinkProfile";
import { convertTiktokLink } from "../../utils/tiktok";

const LinksManager: React.FC = () => {
  const [editingProfile, setEditingProfile] = useState<LinkProfile>({
    name: "main",
  } as LinkProfile);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrCodeSize, setQrCodeSize] = useState(2048);
  const [videoFrameUrl, setVideoFrameUrl] = useState<string>("");
  const [pageSize] = useState(8);
  const [isAdd, setIsAdd] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm();

  const {
    page,
    setPage,
    loading,
    linkProfiles,
    getLinkProfiles,
    addLinkProfile,
  } = useLinkProfile();

  const tblRef: Parameters<typeof Table>[0]["ref"] = React.useRef(null);

  useEffect(() => {
    fetchLinkProfiles();
  }, []);

  const fetchLinkProfiles = async (scrollToBottom: boolean = false) => {
    getLinkProfiles()
      .then((res) => {
        if (!scrollToBottom) {
          return;
        }

        const totalPages = Math.ceil(res.length / pageSize);
        setPage(totalPages);
        if (tblRef.current) {
          tblRef.current.scrollTo({ index: -1 });
        }
      })
      .catch((err) => {
        console.error("Failed to load links", err);
        message.error("Failed to load links");
      });
  };

  const handleConvertTikTokLinks = async (profile: Partial<LinkProfile>) => {
    if (profile.video_link) {
      const convertedLink = await convertTiktokLink(profile.video_link);
      if (!convertedLink) {
        showErrorModal("Invalid TikTok video link");
        return null;
      } else {
        profile.video_link = convertedLink;
      }
    }

    return profile;
  };

  const handleSubmit = () => {
    if (isAdd) {
      handleAddLinkProfile();
    } else {
      handleEditProfile();
    }
  };

  const handleAddLinkProfile = async () => {
    const convertedProfile = await handleConvertTikTokLinks(editingProfile);
    if (!convertedProfile) {
      return;
    }

    addLinkProfile(convertedProfile as LinkProfileCreate)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile added successfully");
      })
      .catch((err) => {
        console.error("Failed to add link profile", err);
        showErrorModal("Failed to add link profile");
      });
  };

  const videoFrame = () => {
    return (
      <>
        <div
          style={{
            position: "relative",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        >
          <iframe
            src={videoFrameUrl}
            allowFullScreen={false}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
        {/* <div
          style={{
            position: 'relative',
            bottom: '30px',
            right: '20px',
            background: 'rgba(0, 0, 0, 0.7)',
            padding: '10px',
            borderRadius: '50%',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '60px',
            height: '60px',
            cursor: 'pointer',
            transition: 'transform 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <QRCodeSVG
            value="https://vioo.vip/register"
            size={40}
            level="H"
            bgColor="transparent"
            fgColor="white"
          />
        </div> */}
      </>
    );
  };

  const handleToggleActive = async (id: number, active: boolean) => {
    try {
      await api.patch(`/v1/link_profile/${id}/`, { active: !active });
      fetchLinkProfiles();
      message.success(
        `Link ${active ? "deactivated" : "activated"} successfully`
      );
    } catch (err) {
      console.error("Failed to update link", err);
      message.error("Failed to update link status");
    }
  };

  const handleDeleteLink = async (id: number) => {
    try {
      await api.delete(`/v1/link_profile/${id}/`);
      fetchLinkProfiles();
      message.success("Link profile deleted successfully");
    } catch (err) {
      console.error("Failed to delete link", err);
      showErrorModal("Failed to delete link profile");
    }
  };

  const handleEditProfile = async () => {
    try {
      if (!editingProfile) return;

      const convertedProfile = await handleConvertTikTokLinks(editingProfile);
      if (!convertedProfile) {
        return;
      }

      await api.put(`/v1/link_profile/${editingProfile.id}/`, convertedProfile);
      setEditModalVisible(false);
      fetchLinkProfiles();
      message.success("Link profile updated successfully");
    } catch (err) {
      console.error("Failed to update link", err);
      showErrorModal("Failed to update link profile");
    }
  };

  const showQrCode = (url: string) => {
    setQrCodeUrl(url);
    setQrModalVisible(true);
  };

  const handlePreviewLink = (path: string, username: string) => {
    let url = `${BASE_URL}/${username}`;
    if (path) {
      url += `/${path}`;
    }
    setVideoFrameUrl(url);
  };

  const handleDuplicateProfile = async (profile: LinkProfile) => {
    if (!profile) {
      message.error("profile are required");
      return;
    }
    const { id, user_profile, ...profileCopy } = profile;
    const maxId = linkProfiles ? Math.max(...linkProfiles.map((p) => p.id)) : 0;

    profileCopy.name = `${profileCopy.name} (${maxId + 1})`;
    profileCopy.path = `${profileCopy.path}-${maxId + 1}`;

    await api
      .post("/v1/link_profile/", profileCopy)
      .then(() => {
        fetchLinkProfiles();
        message.success("Link profile duplicated successfully");
      })
      .catch((err) => {
        console.error("Failed to duplicate link", err);
        showErrorModal("Failed to duplicate link profile");
      });
  };

  const generateQRCodeAsImage = async (
    qrUrl: string,
    filename: string
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      // Create a temporary div to render the QR code
      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.left = "-9999px";
      document.body.appendChild(tempDiv);

      // Render the QR code
      const qr = (
        <QRCodeCanvas
          value={qrUrl}
          size={qrCodeSize}
          level="H"
          bgColor="#ffffff"
          fgColor="#000000"
          includeMargin={true}
        />
      );

      // Use ReactDOM to render the QR code
      const root = ReactDOM.createRoot(tempDiv);
      root.render(qr);

      // Wait for the QR code to be rendered
      setTimeout(() => {
        const canvas = tempDiv.querySelector("canvas");
        if (canvas) {
          // Create a new canvas for the combined image
          const combinedCanvas = document.createElement("canvas");
          const ctx = combinedCanvas.getContext("2d");
          if (!ctx) return;

          const imgSize = qrCodeSize * 1.25;

          // Set canvas size to accommodate QR code and text
          const padding = 80; // Padding around QR code
          const textHeight = 60; // Height for text
          combinedCanvas.width = imgSize + padding * 2;
          combinedCanvas.height = imgSize + padding * 2 + textHeight;

          // Fill white background
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);

          // Draw QR code
          ctx.drawImage(canvas, padding, padding);

          // Draw URL text
          ctx.fillStyle = "#333333";
          ctx.font = "48px Arial";
          ctx.textAlign = "center";

          // Split URL into multiple lines if too long
          const maxWidthQR = combinedCanvas.width - padding * 2;
          const words = qrUrl.split("/");
          let line = "";
          let y = imgSize + padding * 2 + 30; // Starting y position for text

          for (const word of words) {
            const testLine = line + word + "/";
            const metrics = ctx.measureText(testLine);

            if (metrics.width > maxWidthQR && line !== "") {
              ctx.fillText(line, combinedCanvas.width / 2, y);
              line = word + "/";
              y += 30;
            } else {
              line = testLine;
            }
          }
          ctx.fillText(line, combinedCanvas.width / 2, y);

          // Convert to blob
          combinedCanvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            }
            // Clean up
            document.body.removeChild(tempDiv);
          }, "image/png");
        }
      }, 100);
    });
  };

  const downloadAllQRCodes = async () => {
    if (!linkProfiles || linkProfiles.length === 0) {
      message.warning("No links available to generate QR codes");
      return;
    }

    try {
      message.loading({
        content: "Generating QR codes...",
        key: "qrGeneration",
      });

      const zip = new JSZip();
      const qrFolder = zip.folder("qr-codes");

      for (const profile of linkProfiles) {
        let url = `${BASE_URL}/${profile.user_profile.username}`;
        if (profile.path) {
          url += `/${profile.path}`;
        }

        const qrBlob = await generateQRCodeAsImage(
          url,
          `${profile.path || "main"}.png`
        );
        const filename = `${profile.path || "main"}.png`;
        qrFolder?.file(filename, qrBlob);
      }

      const content = await zip.generateAsync({ type: "blob" });
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(content);
      downloadLink.download = "qr-codes.zip";
      downloadLink.click();

      message.success({
        content: "QR codes downloaded successfully!",
        key: "qrGeneration",
      });
    } catch (error) {
      console.error("Error generating QR codes:", error);
      message.error({
        content: "Failed to generate QR codes",
        key: "qrGeneration",
      });
    }
  };

  const columns = [
    // {
    //   title: 'Name',
    //   dataIndex: 'name',
    //   key: 'name',
    // },
    // {
    //   title: 'Clicks',
    //   dataIndex: 'link_counter',
    //   key: 'link_counter',
    // },
    {
      title: "Path",
      dataIndex: "path",
      key: "path",
      render: (path: string) => (path === "" ? "(Main link)" : `/${path}`),
    },
    {
      title: "Full Link",
      dataIndex: "path",
      key: "id",
      render: (path: string, item: LinkProfile) => {
        let url = `${BASE_URL}/${item.user_profile.username}`;
        if (path) {
          url += `/${path}`;
        }

        return (
          <div className="flex items-center justify-between gap-2 max-w-[400px]">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline truncate"
            >
              {url}
            </a>
            <Button
              onClick={(e) => {
                e.preventDefault();
                showQrCode(url);
              }}
            >
              Gen QR Code
            </Button>
          </div>
        );
      },
    },
    {
      title: "URL",
      dataIndex: "video_link",
      key: "video_link",
      render: (url: string, item: LinkProfile) => (
        <div className="max-w-[300px]">
          <div className="truncate">
            Video URL:
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline"
            >
              {" " + url}
            </a>
          </div>
          <div className="truncate">
            Guide URL:
            <a
              href={item.guide_link}
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline"
            >
              {" " + item.guide_link}
            </a>
          </div>
          <div className="truncate">
            About URL:
            <a
              href={item.about_link}
              target="_blank"
              rel="noreferrer"
              className="text-blue-500 hover:underline"
            >
              {" " + item.about_link}
            </a>
          </div>
        </div>
      ),
    },
    {
      title: "Enabled",
      key: "active",
      render: (record: LinkProfile) => (
        <Switch
          checked={record.active}
          onChange={() => handleToggleActive(record.id, record.active)}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: LinkProfile) => (
        <>
          <Button
            type="link"
            disabled={!record.active}
            onClick={() => {
              handlePreviewLink(record.path, record.user_profile.username);
            }}
          >
            Preview
          </Button>
          <Button
            disabled={record.path === ''}
            type="link"
            onClick={() => {
              handleDuplicateProfile(record);
            }}
          >
            Duplicate
          </Button>

          <Button
            type="link"
            onClick={() => {
              setEditingProfile(record);
              form.setFieldsValue(record)
              setIsAdd(false);
              setEditModalVisible(true);
            }}
          >
            Edit
          </Button>

          <Popconfirm
            title="Are you sure you want to delete this link?"
            onConfirm={() => handleDeleteLink(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Layout>
      <HorizontalContainer>
        <MainFrame>
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="px-4 py-6 sm:px-0">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold text-gray-900">
                  Manage Links
                </h1>
              </div>
              <div className="flex justify-end mt-4 mb-4 gap-2">
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingProfile({} as LinkProfile);
                    form.setFieldsValue(editingProfile)
                    setIsAdd(true);
                    setEditModalVisible(true);
                  }}
                >
                  Add Link
                </Button>
                <Button
                  type="primary"
                  onClick={downloadAllQRCodes}
                  // icon={<QRCodeSVG value="dummy" size={20} />}
                >
                  Download All QR Codes
                </Button>
              </div>

              {loading ? (
                <div className="flex justify-center">
                  <Spin size="large" />
                </div>
              ) : (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={linkProfiles}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: linkProfiles?.length || 0,
                    showSizeChanger: false,
                    showQuickJumper: false,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  scroll={{ x: "max-content", scrollToFirstRowOnChange: false }}
                  style={{ maxWidth: "100%" }}
                  ref={tblRef}
                  onChange={(pagination) => {
                    setPage(pagination.current || 1);
                    // Scroll to bottom when page changes
                    setTimeout(() => {
                      if (tblRef.current) {
                        tblRef.current.scrollTo({ index: -1 });
                      }
                    }, 100);
                  }}
                />
              )}
            </div>
          </div>

          <Modal
            title={(isAdd ? "Add" : "Edit") + " Link Profile"}
            open={isEditModalVisible}
            onOk={handleSubmit}
            onCancel={() => {
              setEditModalVisible(false);
            }}
            width={800}
            okButtonProps={{ disabled: !isFormValid }}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={editingProfile}
              className="grid grid-cols-2 gap-x-6 gap-y-6"
              onValuesChange={(_, allValues) =>
                setEditingProfile({ ...editingProfile, ...allValues })
              }
              onFieldsChange={(_, allFields) => {
                const hasError = form.getFieldsError().some(({ errors }) => errors.length);
                setIsFormValid(!hasError);
              }}
            >
              <Form.Item label="Name" name="name" style={{ display: "none" }}>
                <Input id="edit-name" />
              </Form.Item>
              <Form.Item label="Title" name="title" style={{ display: "none" }}>
                <Input id="edit-title" />
              </Form.Item>
              <Form.Item
                label="Path"
                name="path"
                rules={[{ required: true, message: "Please input the path!" }]}
              >
                <Input id="edit-path" />
              </Form.Item>
              <Form.Item
                label="Video URL"
                name="video_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Video link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-video-url" />
              </Form.Item>
              <Form.Item label="Guide Text" name="guide_text">
                <Input id="edit-guide-text" />
              </Form.Item>
              <Form.Item
                label="Guide URL"
                name="guide_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the Guide link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-guide-url" />
              </Form.Item>
              <Form.Item label="About Text" name="about_text">
                <Input id="edit-about-text" />
              </Form.Item>
              <Form.Item
                label="About URL"
                name="about_link"
                rules={[
                  {
                    required: true,
                    message: "Please input the About link URL!",
                  },
                  {
                    type: "url",
                    message: "Please enter a valid URL!",
                  },
                ]}
              >
                <Input id="edit-about-url" />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="QR Code"
            open={qrModalVisible}
            onCancel={() => setQrModalVisible(false)}
            footer={[
              <Button
                key="download"
                type="primary"
                onClick={() => {
                  const svg = document.querySelector(".qr-code-svg");
                  if (svg) {
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    const img = new Image();
                    img.onload = () => {
                      // Set canvas size to accommodate QR code and text
                      const padding = 80;
                      const textHeight = 60;
                      canvas.width = img.width + padding * 2;
                      canvas.height = img.height + padding * 2 + textHeight;

                      // Fill white background
                      if (ctx) {
                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, canvas.width, canvas.height);

                        // Draw QR code
                        ctx.drawImage(img, padding, padding);

                        // Draw URL text
                        ctx.fillStyle = "#333333";
                        ctx.font = "48px Arial";
                        ctx.textAlign = "center";

                        // Split URL into multiple lines if too long
                        const maxWidthModal = canvas.width - padding * 2;
                        const words = qrCodeUrl.split("/");
                        let line = "";
                        let y = img.height + padding * 2 + 30;

                        for (const word of words) {
                          const testLine = line + word + "/";
                          const metrics = ctx.measureText(testLine);

                          if (metrics.width > maxWidthModal && line !== "") {
                            ctx.fillText(line, canvas.width / 2, y);
                            line = word + "/";
                            y += 30;
                          } else {
                            line = testLine;
                          }
                        }
                        ctx.fillText(line, canvas.width / 2, y);
                      }

                      const pngFile = canvas.toDataURL("image/png");
                      const downloadLink = document.createElement("a");
                      downloadLink.download = `${qrCodeUrl}.png`;
                      downloadLink.href = pngFile;
                      downloadLink.click();
                    };
                    img.src = "data:image/svg+xml;base64," + btoa(svgData);
                  }
                }}
              >
                Download QR Code
              </Button>,
              <Button key="close" onClick={() => setQrModalVisible(false)}>
                Close
              </Button>,
            ]}
          >
            <div className="flex justify-center p-4">
              <QRCodeSVG
                style={{ width: "512px", height: "512px" }}
                value={qrCodeUrl}
                size={qrCodeSize}
                className="qr-code-svg"
              />
            </div>
            <div className="text-center mt-4">
              <p className="text-gray-600 break-all">{qrCodeUrl}</p>
            </div>
          </Modal>
        </MainFrame>
        <PhoneMockup>{videoFrame()}</PhoneMockup>
      </HorizontalContainer>
    </Layout>
  );
};

const MainFrame = styled.div`
  width: 70%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PhoneMockup = styled.div`
  width: 30%;
  position: relative;
  padding: 2rem;
  max-height: 1024px;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("https://vioo.vip/promotion_vip") center/cover;
    border-radius: 30px;
    border: 12px solid #000;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 70vh;
  }
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  min-height: 80vh;
`;

export default LinksManager;
